import { z } from 'zod';
import { compareDates } from '../dates';
import { AutonomyLevel, CheckpointKind, CheckpointStatus, WorkRole, WorkStatus } from '../enums';
import { checkpointKeySchema, policyContentSchema } from '../policy/content';
import { isoDate, nonEmptyText, optionalText, paginationSchema, uuidSchema, queryBoolean } from './common';

const workRoleEnum = z.enum(Object.values(WorkRole) as [WorkRole, ...WorkRole[]]);

export const workSummarySchema = z.object({
  problem: optionalText(1000),
  objective: optionalText(1000),
  method: optionalText(1000),
});

export const workParticipantInputSchema = z.object({ userId: uuidSchema, role: workRoleEnum });

export const createWorkSchema = z
  .object({
    institutionId: uuidSchema,
    programId: uuidSchema,
    modalityId: uuidSchema,
    title: nonEmptyText(300, 'Título'),
    summary: workSummarySchema.default({}),
    startDate: isoDate,
    expectedEndDate: isoDate,
    autonomyLevel: z.enum([AutonomyLevel.FULL_REVIEW, AutonomyLevel.AUTO_LOW_RISK]).optional(),
    participants: z.array(workParticipantInputSchema).min(1).max(30),
    scholarship: z
      .object({ agency: nonEmptyText(80, 'Agência'), endDate: isoDate })
      .nullable()
      .optional(),
    /** Confirma a criação mesmo com alertas (avisos) no cronograma. Erros sempre bloqueiam. */
    acknowledgeWarnings: z.boolean().default(false),
  })
  .refine((v) => compareDates(v.expectedEndDate, v.startDate) > 0, {
    message: 'O término previsto deve ser posterior ao início',
    path: ['expectedEndDate'],
  })
  .refine((v) => v.participants.some((p) => p.role === 'STUDENT'), {
    message: 'Informe ao menos um(a) estudante',
    path: ['participants'],
  })
  .refine((v) => v.participants.filter((p) => p.role === 'LEAD_ADVISOR').length === 1, {
    message: 'Informe exatamente um(a) orientador(a) principal',
    path: ['participants'],
  });
export type CreateWorkInput = z.infer<typeof createWorkSchema>;

export const updateWorkSchema = z.object({
  title: nonEmptyText(300, 'Título').optional(),
  summary: workSummarySchema.optional(),
  autonomyLevel: z.enum([AutonomyLevel.FULL_REVIEW, AutonomyLevel.AUTO_LOW_RISK]).optional(),
  scholarship: z
    .object({ agency: nonEmptyText(80, 'Agência'), endDate: isoDate })
    .nullable()
    .optional(),
});
export type UpdateWorkInput = z.infer<typeof updateWorkSchema>;

export const WORK_STATUS_TRANSITIONS: Record<WorkStatus, WorkStatus[]> = {
  DRAFT: ['ACTIVE', 'CANCELLED'],
  // Confirmação/recusa só pelo fluxo de aprovação (POST /works/:id/approval)
  PENDING_APPROVAL: [],
  ACTIVE: ['SUSPENDED', 'COMPLETED', 'CANCELLED'],
  SUSPENDED: ['ACTIVE', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export const changeWorkStatusSchema = z.object({
  status: z.enum(Object.values(WorkStatus) as [WorkStatus, ...WorkStatus[]]),
  reason: nonEmptyText(1000, 'Justificativa'),
});
export type ChangeWorkStatusInput = z.infer<typeof changeWorkStatusSchema>;

export const listWorksQuerySchema = paginationSchema.extend({
  institutionId: uuidSchema.optional(),
  programId: uuidSchema.optional(),
  modalityId: uuidSchema.optional(),
  status: z.enum(Object.values(WorkStatus) as [WorkStatus, ...WorkStatus[]]).optional(),
  light: z.enum(['GREEN', 'YELLOW', 'RED']).optional(),
  q: z.string().trim().max(120).optional(),
  archived: queryBoolean.default(false),
});
export type ListWorksQuery = z.infer<typeof listWorksQuerySchema>;

export const workExceptionSchema = z.object({
  content: policyContentSchema,
  justification: nonEmptyText(2000, 'Justificativa'),
});
export type WorkExceptionInput = z.infer<typeof workExceptionSchema>;

// ---------------------------------------------------------------- Cronograma

export const CHECKPOINT_STATUS_TRANSITIONS: Record<CheckpointStatus, CheckpointStatus[]> = {
  PLANNED: ['IN_PROGRESS', 'SUBMITTED', 'WAIVED'],
  IN_PROGRESS: ['SUBMITTED', 'PLANNED', 'WAIVED'],
  SUBMITTED: ['APPROVED', 'CHANGES_REQUESTED'],
  CHANGES_REQUESTED: ['SUBMITTED', 'IN_PROGRESS'],
  APPROVED: [],
  WAIVED: [],
};

export const updateCheckpointStatusSchema = z.object({
  status: z.enum(Object.values(CheckpointStatus) as [CheckpointStatus, ...CheckpointStatus[]]),
  note: optionalText(2000),
});
export type UpdateCheckpointStatusInput = z.infer<typeof updateCheckpointStatusSchema>;

export const addCheckpointSchema = z.object({
  key: checkpointKeySchema,
  title: nonEmptyText(160, 'Título'),
  description: optionalText(4000),
  kind: z.enum([CheckpointKind.CHECKPOINT, CheckpointKind.MILESTONE]).default('CHECKPOINT'),
  dueDate: isoDate,
  dependsOn: z.array(checkpointKeySchema).max(20).default([]),
  acceptanceCriteria: optionalText(4000),
  justification: nonEmptyText(1000, 'Justificativa'),
});
export type AddCheckpointInput = z.infer<typeof addCheckpointSchema>;

export const requestExtensionSchema = z.object({
  checkpointId: uuidSchema,
  requestedDueDate: isoDate,
  justification: nonEmptyText(2000, 'Justificativa'),
  requestId: uuidSchema.optional(),
});
export type RequestExtensionInput = z.infer<typeof requestExtensionSchema>;

export const decideExtensionSchema = z.object({
  decision: z.enum(['APPROVE', 'REJECT']),
  approvedDueDate: isoDate.optional(),
  note: optionalText(2000),
});
export type DecideExtensionInput = z.infer<typeof decideExtensionSchema>;

export const extensionImpactQuerySchema = z.object({ checkpointId: uuidSchema, newDueDate: isoDate });

export const migrationApplySchema = z.object({
  /** Hash da política alvo exibida na simulação — garante que se aplica o que foi visto. */
  targetHash: z.string().min(8).max(64),
  justification: nonEmptyText(1000, 'Justificativa'),
});
export type MigrationApplyInput = z.infer<typeof migrationApplySchema>;
